
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import { StatsCard } from '../components/StatsCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { Pagination } from '../components/Pagination';
import { agentsAPI } from '../lib/api';
import { STATUS } from '../lib/theme';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { id } from 'date-fns/locale';
import {
    Server,
    Wifi,
    WifiOff,
    Activity,
    Laptop,
    Trash2,
    Search,
    Edit2,
    Save,
} from 'lucide-react';
import { toast } from 'sonner';

// WITA Timezone
const WITA_TIMEZONE = 'Asia/Makassar';

export function AgentHealthPage() {
    const [loading, setLoading] = useState(true);
    const [agents, setAgents] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [stats, setStats] = useState({ total: 0, online: 0, offline: 0 });
    const [searchQuery, setSearchQuery] = useState('');
    const [editingAgent, setEditingAgent] = useState(null);
    const [editAlias, setEditAlias] = useState('');

    const loadData = React.useCallback(async () => {
        try {
            const response = await agentsAPI.getAll({
                page: currentPage,
                per_page: 5,
                q: searchQuery
            });

            // Handle response structure
            if (response.data.agents) {
                setAgents(response.data.agents);
                setTotalPages(response.data.total_pages);
                if (response.data.stats) {
                    setStats(response.data.stats);
                }
            } else {
                // Fallback for non-paginated API (just in case)
                setAgents(response.data);
            }
        } catch (error) {
            console.error('Failed to load agents:', error);
            toast.error('Gagal memuat data server');
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchQuery]);

    useEffect(() => {
        // Debounce search
        const delayDebounceFn = setTimeout(() => {
            loadData();
        }, 500);
        return () => clearTimeout(delayDebounceFn);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadData]);

    useEffect(() => {
        // Auto refresh interval (only if no search query to avoid jitter)
        if (!searchQuery) {
            const interval = setInterval(loadData, 30000);
            return () => clearInterval(interval);
        }
    }, [searchQuery, loadData]);



    const handleDelete = async (agentId, agentName) => {
        if (!window.confirm(`Apakah Anda yakin ingin menghapus agent "${agentName}"?\nSemua sertifikat terkait juga akan dihapus.`)) {
            return;
        }
        try {
            const response = await agentsAPI.delete(agentId);
            if (response.data.success) {
                toast.success('Agent berhasil dihapus');
                loadData();
            } else {
                toast.error(response.data.message || 'Gagal menghapus agent');
            }
        } catch (error) {
            toast.error('Terjadi kesalahan saat menghapus agent');
        }
    };

    const handleEditAlias = (agent) => {
        setEditingAgent(agent);
        setEditAlias(agent.alias || '');
    };

    const handleSaveAlias = async () => {
        if (!editingAgent) return;

        try {
            const response = await agentsAPI.updateAlias(editingAgent.id, editAlias);
            if (response.data.success) {
                toast.success(
                    <div className="flex flex-col gap-1">
                        <div className="font-semibold">✅ Alias Berhasil Diperbarui!</div>
                        <div className="text-sm">
                            Server: <span className="font-medium">{editingAgent.server_source}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                            Alias baru: {editAlias || '(tidak ada)'}
                        </div>
                    </div>,
                    { duration: 4000 }
                );
                setEditingAgent(null);
                setEditAlias('');
                loadData();
            } else {
                toast.error(response.data.message || 'Gagal memperbarui alias');
            }
        } catch (error) {
            toast.error(
                <div className="flex flex-col gap-1">
                    <div className="font-semibold">❌ Gagal Memperbarui Alias</div>
                    <div className="text-xs text-muted-foreground mt-1">
                        {error.response?.data?.message || error.message || 'Terjadi kesalahan'}
                    </div>
                </div>,
                { duration: 5000 }
            );
        }
    };

    const handleCancelEdit = () => {
        setEditingAgent(null);
        setEditAlias('');
    };

    const formatLastSeen = (timestamp) => {
        if (!timestamp) return 'Belum pernah';
        try {
            const date = new Date(timestamp);
            const witaDate = toZonedTime(date, WITA_TIMEZONE);
            return format(witaDate, 'dd MMM yyyy, HH:mm', { locale: id }) + ' WITA';
        } catch (e) {
            return timestamp;
        }
    };

    if (loading) {
        return <LoadingSpinner text="Memuat data server..." />;
    }

    return (
        <div className="space-y-4">
            {/* Breadcrumb + Page Header */}
            <div>
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1">
                    <span>Manajemen</span>
                    <span>›</span>
                    <span style={{ color: STATUS.primary.text }} className="font-medium">Status Server Agent</span>
                </div>
                <h2 className="text-base sm:text-lg font-semibold tracking-tight">Status Server Agent</h2>
                <p className="text-muted-foreground mt-0.5 text-xs">
                    Monitor kesehatan dan status koneksi server agent
                </p>
            </div>

            {/* Stats Cards */}
            <div className="grid gap-3 grid-cols-1 sm:grid-cols-3">
                <StatsCard
                    title="Total Server"
                    value={stats.total}
                    description="Terdaftar di sistem"
                    icon={Server}
                    status="primary"
                />
                <StatsCard
                    title="Online"
                    value={stats.online}
                    description="Aktif dalam 24 jam terakhir"
                    icon={Wifi}
                    status="valid"
                />
                <StatsCard
                    title="Offline"
                    value={stats.offline}
                    description="Tidak merespon > 24 jam"
                    icon={WifiOff}
                    status="critical"
                />
            </div>

            {/* Agents List */}
            <Card>
                <CardHeader>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div>
                            <CardTitle>Daftar Server Agent</CardTitle>
                            <CardDescription>
                                Daftar semua server yang menjalankan script monitoring SSL
                            </CardDescription>
                        </div>
                        <div className="relative w-full md:w-64">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                            <Input
                                type="text"
                                placeholder="Cari server..."
                                className="pl-8"
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        {agents.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground gap-2">
                                <Laptop className="h-8 w-8 opacity-30" />
                                <p className="text-xs">
                                    {searchQuery ? 'Tidak ada agent yang cocok dengan pencarian.' : 'Belum ada server agent yang terdaftar.'}
                                </p>
                            </div>
                        ) : (
                            <div className="grid gap-2">
                                {agents.map((agent) => {
                                    const colors = agent.status === 'online' ? STATUS.valid : STATUS.critical;
                                    return (
                                        <div
                                            key={agent.id}
                                            className="flex items-center justify-between p-3 border rounded-sm hover:bg-accent/50 transition-colors"
                                        >
                                            <div className="flex items-start gap-3">
                                                <div
                                                    className="p-2 rounded-sm mt-0.5"
                                                    style={{ background: colors.bg, color: colors.icon }}
                                                >
                                                    <Laptop className="h-4 w-4" />
                                                </div>
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="font-semibold text-xs">
                                                            {agent.alias || agent.server_source}
                                                        </h4>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="h-6 w-6 text-muted-foreground hover:text-primary"
                                                            onClick={() => handleEditAlias(agent)}
                                                            title="Edit Alias"
                                                        >
                                                            <Edit2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </div>
                                                    {agent.alias && (
                                                        <p className="text-xs text-muted-foreground mt-0.5">
                                                            ID Server: {agent.server_source}
                                                        </p>
                                                    )}
                                                    <p className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                                                        {agent.agent_address}
                                                    </p>
                                                    <div className="mt-1 flex items-center gap-4 text-xs text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <Activity className="h-3 w-3" />
                                                            Memantau {agent.certificates_count} sertifikat
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3">
                                                <div className="text-right hidden sm:block">
                                                    <Badge
                                                        style={{ background: colors.bg, borderColor: colors.border, color: colors.text }}
                                                    >
                                                        {agent.status === 'online' ? 'ONLINE' : 'OFFLINE'}
                                                    </Badge>
                                                    <p className="text-xs text-muted-foreground mt-2">
                                                        Last seen: {formatLastSeen(agent.last_seen)}
                                                    </p>
                                                </div>

                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-7 w-7 hover:text-destructive"
                                                    onClick={() => handleDelete(agent.id, agent.server_source)}
                                                    title="Hapus Agent"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <div className="mt-4">
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Edit Alias Dialog */}
            <Dialog open={!!editingAgent} onOpenChange={(open) => !open && handleCancelEdit()}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Edit Alias Server</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-3">
                        <div>
                            <label className="text-xs font-medium text-muted-foreground">
                                ID Server (Original)
                            </label>
                            <p className="text-xs font-mono bg-muted p-2 rounded-sm mt-1">
                                {editingAgent?.server_source}
                            </p>
                        </div>

                        <div>
                            <label className="text-xs font-medium" htmlFor="alias-input">
                                Alias (Nama Kustom)
                            </label>
                            <Input
                                id="alias-input"
                                type="text"
                                className="mt-1"
                                placeholder="Masukkan nama kustom (opsional)"
                                value={editAlias}
                                onChange={(e) => setEditAlias(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleSaveAlias();
                                }}
                                autoFocus
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Kosongkan untuk menggunakan ID Server sebagai nama
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={handleCancelEdit}>
                            Batal
                        </Button>
                        <Button onClick={handleSaveAlias}>
                            <Save className="h-3.5 w-3.5 mr-1.5" />
                            Simpan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
